import React from 'react';
import {
	Box,
	ComposableTypography,
	Spacer,
	Typography,
	useTheme,
} from '@bcp-nextgen-dx-component-factory/accolade-design-system';
import { DisplayCost } from './displayCost';
import { useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { FormatHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/$helpers';
import { Anything, SolutionsType } from '@types';
import { rem } from '@helpers';
import { useSeparateLastWordFromPhrase } from '@hooks';

export interface CostSummaryIntegratedSolutionProps {
	name: string;
	monthsPromotion: number;
	discountPrice: number;
	mainPrice: number;
	hasDiscount: boolean;
	solutionId: SolutionsType;
}

const flexSettings = {
	justifyContent: 'space-between',
	alignItems: 'center',
};

export const CostSummaryIntegratedSolution: React.FC<CostSummaryIntegratedSolutionProps> = (props) => {
	const { name, monthsPromotion, discountPrice, mainPrice, hasDiscount, solutionId } = props;
	const { palette } = useTheme();
	const t = useTranslation();
	const appearancePlanName = 'subtitle-100-04';
	const resources = {
		discountDurationText: t('placeholders.costSummary.promotionalCodeDuration', {
			months: monthsPromotion,
		}),
		costAfterDiscount: t('placeholders.costSummary.costAfterPromotionalCode', {
			month: Number(monthsPromotion) + 1,
		}),
		discountMonthlyAmount: FormatHelper.formatNumber(discountPrice, 'amount'),
		mainPriceAmount: FormatHelper.formatNumber(mainPrice, 'amount'),
		solutionName: t('placeholders.enterpriseSolution.card.subtitle', { planName: name }),
	};
	const { phraseWithoutLastWord: solutionNameWithoutLastWord, lastWord: solutionNameLastWord } =
		useSeparateLastWordFromPhrase(resources.solutionName);

	let solutionNameElement: JSX.Element = (
		<Typography appearance={appearancePlanName}>{resources.solutionName}</Typography>
	);

	if (solutionId === '201') {
		solutionNameElement = (
			<ComposableTypography
				text={`${solutionNameWithoutLastWord} <highlight>${solutionNameLastWord}</highlight>`}
				baseProps={{
					appearance: appearancePlanName,
				}}
				tags={{
					highlight: {
						appearance: appearancePlanName,
						alternativeStyleProperties: {
							color: palette.global.ui1,
						} as Anything,
					},
				}}
			/>
		);
	}

	return (
		<>
			{hasDiscount && (
				<>
					<Box styles={{ ...flexSettings }}>
						{solutionNameElement}
						<Box
							styles={{
								margin: '0 16px 0 auto',
								borderRadius: '4px',
								backgroundColor: '#D1005D33',
								padding: '2px 8px',
							}}
						>
							<Typography appearance="microcopy-75-07" colorsLevel="secondary">
								{resources.discountDurationText}
							</Typography>
						</Box>
						<DisplayCost cost={resources.discountMonthlyAmount} appearance={'promotionalMainSolution'} />
					</Box>
					<Spacer vertical={rem(16)} />
				</>
			)}
			<Box styles={{ ...flexSettings }}>
				<Typography
					appearance={hasDiscount ? 'body-100-03' : 'subtitle-100-04'}
					alternativeStyleProperties={{ color: hasDiscount ? '#80828F' : '#2E3641' } as Anything}
				>
					{hasDiscount ? resources.costAfterDiscount : name}
				</Typography>
				<DisplayCost
					cost={hasDiscount ? resources.mainPriceAmount : mainPrice}
					appearance={hasDiscount ? 'secondarySolution' : 'main'}
				/>
			</Box>
		</>
	);
};
